import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './article.css';
import Amalgam from '../../assets/images/Amalgam-logo.png';
import LinkedIn from '../../assets/images/LinkedIn.png';
import Breadcrumb from '../../assets/components/BreadCrumb';
import { useMediaQuery } from 'react-responsive';
import Arrow from '../../assets/images/Arrow1.png';
import { useNavigate } from 'react-router-dom';

function Barclays() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const sectionRef = useRef<HTMLElement>(null);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const sections = entry.target.getElementsByClassName('content-section');
                    Array.from(sections).forEach((section) => {
                        if (entry.isIntersecting) {
                            section.classList.add('animate');
                        } else {
                            section.classList.remove('animate');
                        }
                    });
                });
            },
            {
                threshold: 0.2
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <div className='article-container' >
            <div>
                <Breadcrumb route="/case-studies" pageTitle="Barclays Bank US" />
            </div>
            <section className='article-row-1' id='barclays-background'>
                <p>Banking</p>
                <h2>Barclays Bank US</h2>
                <div className="article-details fade-in-right">
                    <div className="article-detail-item">
                        <span>Location: New York, New York 🇺🇲</span>
                    </div>
                </div>
            </section>


            <section className='article-intro-section'>
                <h1>How The Website Revamp And CMS We Implemented Enabled Barclay's to Cater to its Diverse Customers</h1>
                <p>
                    Barclays Bank is a global financial services provider offering a broad range of products. These include anything from high-yield savings accounts, to certificates of deposit (CDs), and credit cards. These services cater to individual consumers as well as businesses, which positions Barclays Bank US as a B2C and B2B financial institution.
                </p>
            </section>

            <section className='article-row-2' ref={sectionRef}>
                <div className='content-section'>
                    <h3>Challenge</h3>
                    <p>Barclays Bank US had an outdated marketing website. The UX and UI weren't intuitive, and it lacked accessibility compliance and personalization capabilities. It prevented the bank from showcasing its rates, security features, and unique services effectively. Barclays Bank US risked its customer engagement and brand reputation to take a hit, possibly resulting in the loss of business opportunities.</p>
                </div>
                <div className='content-section'>
                    <h3>Approach</h3>
                    <p>We redesigned Barclays Bank US's website to create a smooth and engaging experience, adding features along with a fresh, modern look. We also made it simpler to manage and update content, allowing customers to access tailored information and enjoy a user-friendly portal with a sleek design.</p>
                </div>
                <div className='content-section'>
                    <h3>Results</h3>
                    <p>The redesigned website empowered Barclays Bank US to stand out in a competitive market, offering customers an optimized online banking experience. This enhanced their customer engagement and positioned them as a modern and customer-centric bank.</p>
                </div>
            </section>
            <section className='article-section-6'>
                <h2>The Challenge</h2>
                <h3>Barclays Bank US, a prominent financial institution, had set forth on its mission to cater to diverse financial needs, offering a wide array of services like personal and business banking, student loans, home equity products, credit cards, and more.</h3>
                <p>
                    Barclays Bank US understood that they had to revamp their marketing website in order to be fully prepared to serve their customers. Their aim was to deliver an elevated and seamless banking experience that would enhance their customers' financial journeys.
                    A website revamp would allow them to better communicate their ability to meet the rates and security needs of their prospective clients, add value, and better depict their uniqueness from a visual perspective. This would help Barclays Bank US stand out in the market and create a stronger sense of accessibility for their users.
                </p>
                <p>
                    The reasons why these issues called for a prompt fix were multiple, as Barclays Bank US wanted to avoid facing:
                </p>
                <ul>
                    <li>A competitive disadvantage due to their inability to effectively convey their unique offerings and security features</li>
                    <li>Lost business opportunities because of their sub-optimal website</li>
                    <li>A negative brand perception, since they could have appeared as less modern and customer-centric than they are</li>
                    <li>Decreased customer engagement caused by their website not being engaging and informative enough</li>
                </ul>
            </section>

            <section className='article-section-3'>

                <h2>The Solution</h2>
                <h3>Our Approach:</h3>
                <p>
                    We started off by revamping Barclays Bank US's marketing website, creating a seamless and dynamic user experience with CSS transitions, parallax scrolling, and intelligent page indicators, all while ensuring a modern and tech-focused identity through accessible and technology-centric images, and rounded corners for a contemporary touch.
                    Our team personalized the banking experience by incorporating valuable tools like goal setters, FDIC maximizers, and CD ladder builders. We integrated rate comparisons from Bankrate and product bundles with flexibility for future use, offering customers a comprehensive range of options.
                </p>
                <p>
                    We then implemented Magnolia CMS capabilities, enabling rules for diverse copy and image delivery across the site. Barclays Bank US's website's personalization features recognized users, emphasizing unowned products and highlighting new tools tailored to individual needs.
                </p>
                <p>
                    We also ensured that Barclays Bank US's content management system, Magnolia, supported infographics, videos, blogging formats, and integrated social media content.
                </p>
                <p>
                    Finally, we wrapped the architecture behind the customer portal with a contemporary UX/UI design.
                </p>

                <h3>Our Step-by-Step Process:</h3>
                <ul>
                    <li>UX/UI design and front-end development.</li>
                    <li>The incorporation of new functionalities and tools.</li>
                    <li>Customization and optimized website responsiveness.</li>
                    <li>We ensured that the bank's content management system would support infographics, videos, blogging formats, and integrated social media content.</li>
                </ul>

                <h3>The Results</h3>
                <p>
                    Our team leveraged its expertise in designing and developing marketing websites that not only instantly communicate clients' capabilities but also empower businesses through streamlined content updates, eliminating coordination hassles, and expediting deployment processes.
                </p>
                <p>
                    We ensured an inclusive and visitor-friendly digital experience, enabling the website to seamlessly display across various screen devices in adherence to WCAG standards.
                </p>
                <p>
                    We built a clear and concise homepage, showcasing Savings and CD overviews with market-leading rates, minimum deposits, FDIC insurance, and transparent fees. The page includes intuitive CTAs for easy access, interactive tools, security information, and contact details to talk to a specialist.
                </p>
            </section>

            <section className='article-section-4'>
                <h2>Conclusions</h2>

                <h3>Summary of Benefits</h3>
                <p>
                    We delivered an elevated and seamless banking experience to enhance our client's customers' financial journeys. Barclays Bank US could now leverage its site to appropriately showcase its rates and unique services and come across as a secure and dependable financial services provider to its customers. We helped the bank streamline their content updates, ensured accessibility compliance, and accelerated deployment to help them stand out, offer personalized guidance, and deliver an exceptional online banking experience to their customers.
                </p>

                <h3>Contact Information</h3>
                <p>
                    Are you ready to leverage cutting-edge technology for your organization's needs?
                    Contact us at <a href="mailto:hello@amalgam-inc.com">hello@amalgam-inc.com</a>.
                </p>
            </section>

            <section className='ourwork-row-14 blog-row-14'>

                <h2>Get in touch</h2>
                <p>Reach out to us to gather more information or <br className='blog-row-14-break' />  set our wheels in motion.</p>
                <div className='ourwork-arrow-box blog-arrow-box'>
                    <img src={Arrow} alt="arrow" className='ourwork-arrow' />
                    <button onClick={() => navigate("/contactus")}>Let's talk</button>
                </div>

            </section>

            {!isMobile ? (
                <section className='home-footer'>
                    <div className='home-footer-middle'>
                        <img src={Amalgam} alt="Amalgam" className='home-footer-amalgam' />
                        <Link to="/" className='home-footer-links'>Home</Link>
                        <Link to="/ourwork" className='home-footer-links'>Our Work</Link>
                        <Link to="/aboutus" className='home-footer-links'>About Us</Link>
                        <Link to="/case-studies" className='home-footer-links'>Case Studies</Link>
                        <Link to="/startups" className='home-footer-links'>Launchpad</Link>
                        <Link to="/careers" className='home-footer-links'>Careers</Link>
                        <Link to="/contactus" className='home-footer-links'>Contact Us</Link>
                        <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' />
                    </div>
                    <div className='home-footer-dividor'>
                        <div className='home-footer-policies'>
                            <p><Link to="/privacy-policy">Privacy Policy</Link></p>
                            <p><Link to="/terms-and-conditions">Terms and Conditions</Link></p>
                            <p><Link to="/cookie-policy">Cookie Policy</Link></p>
                        </div>
                    </div>
                </section>
            ) : (
                <section>
                    <div className="footer-minimal">
                        <div className="footer-content">
                            {/* <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px' }}>
                <img src={Amalgam} alt="Amalgam" className="footer-logo" />
                <img src={LinkedIn} alt="Instagram" className='home-footer-linkedin' style={{ alignSelf: "center" }} />
              </div> */}
                            <div className="footer-top-row">
                                <Link to="/" className="footer-link">Home</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/ourwork" className="footer-link">Our Work</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/aboutus" className="footer-link">About Us</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/case-studies" className="footer-link">Case Studies</Link>
                            </div>
                            <div className="footer-bottom-row">
                                <Link to="/startups" className="footer-link">Launchpad</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/careers" className="footer-link">Careers</Link>
                                <span className="footer-separator">•</span>
                                <Link to="/contactus" className="footer-link">Contact</Link>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        width: "80%",
                        height: "0px",
                        borderTop: "1px solid black",
                        backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        paddingBottom: "20px",
                        margin: "0 auto"
                    }}>
                    </div>
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "white",
                        gap: isMobile ? "5px" : "20px",
                        paddingBottom: "20px",
                        width: "100%",
                        padding: "0 10px 20px 10px",
                        textAlign: "center"
                    }}>
                        <Link to="/privacy-policy" style={{ textAlign: "center", color: "black", textDecoration: "none" }}>Privacy Policy</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/terms-and-conditions" style={{ color: "black", textDecoration: "none" }}>Terms and Conditions</Link>
                        <span style={{ color: "black", fontSize: "14px" }}>|</span>
                        <Link to="/cookie-policy" style={{ fontSize: "14px", color: "black", textDecoration: "none" }}>Cookie Policy</Link>
                    </div>
                </section>
            )}
        </div>
    );
}

export default Barclays;